import { FieldListObject } from '@fabric-ui/components';
import { entityProjectionNames } from '@utils/entityProjectionNames';
import type { AvPermissionsType } from '../../context/AvContext.types';
import { Permission, PermissionEntitiesNames, PermissionModelEntityName } from '../../types';

export {
  getAssetScoreField,
  getFindingScoreField,
  getIdentityFindingScoreField,
  getTenantScoreField,
  getTicketScoreField,
} from '@views/Tickets/ticket.types';

export const hasPermissionToReadTicketOutegrations = ({
  projName,
  hasAllowedPermissionToResource,
}: {
  projName: string;
  hasAllowedPermissionToResource?: AvPermissionsType['hasAllowedPermissionToResource'];
}) =>
  projName !== entityProjectionNames.Ticket ||
  (typeof hasAllowedPermissionToResource === 'function' &&
    (hasAllowedPermissionToResource({ resource: PermissionEntitiesNames.INTEGRATION, permission: Permission.READ }) ||
      hasAllowedPermissionToResource({
        resource: PermissionEntitiesNames.INT,
        category: PermissionModelEntityName.TICKET,
        permission: Permission.CREATE,
      }) ||
      hasAllowedPermissionToResource({
        resource: PermissionEntitiesNames.INT,
        category: PermissionModelEntityName.TICKET,
        permission: Permission.UPDATE,
      })));

const INTERNAL_SCHEMA_MESSAGE_PREFIX = '.io.avalor.schema.';

export const isSchemaMessageType = field => !!field.originalType?.name?.startsWith(INTERNAL_SCHEMA_MESSAGE_PREFIX);

export const filterJsonFields = field => !['["MESSAGE"]', 'MESSAGE'].includes(field.type?.toString()) && !isSchemaMessageType(field);

export const getFilteredOneToOneFields = (fieldListWithOneToOne: FieldListObject[], excludeList, fields) =>
  fieldListWithOneToOne.filter(
    v =>
      !v.visibilityConfig.config.hidden &&
      ![...(excludeList?.aliases || []), ...(excludeList?.fields || [])].some(substring => v.value.includes(substring)) &&
      !fields.fields.flatMap(v => v.map(({ field }) => field)).includes(v.value) &&
      filterJsonFields(v)
  );

export const mergeFieldValuesWithComma = (
  data: Array<Record<string, unknown>>
): Record<string, string | number | boolean | null | undefined> => {
  if (!Array.isArray(data) || data.length === 0) {
    return {};
  }

  const allKeys = [...new Set(data.flatMap(obj => Object.keys(obj)))];
  const merged: Record<string, string | number | boolean | null | undefined> = {};

  allKeys.forEach(key => {
    const values = Array.from(new Set(data.map(obj => obj[key]).filter(value => value != null && value !== '')));

    if (values.length === 0) {
      return;
    }

    if (values.every(v => v === values[0])) {
      const [firstValue] = values;
      merged[key] = firstValue as string | number | boolean | null | undefined;
    } else {
      merged[key] = values.map(String).join(', ');
    }
  });

  return merged;
};
